// The web app's entry: Automerge's wasm first (from the service worker's cache when offline), the service worker, then
// React.
import * as A from '@automerge/automerge';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './shell/App.tsx';
import { applyAppearance } from './shell/appearance.ts';
import { applyContrast } from './shell/contrast.ts';
import { applyFace } from './shell/face.ts';
import './styles/app.css';
import './styles/plan.css';
import './styles/story.css';
import './styles/revise.css';
import './styles/members.css';
import './styles/filter.css';
import './styles/maps.css';

declare const __AUTOMERGE_WASM__: string;

interface TrustedTypePolicyFactory {
  createPolicy(name: string, rules: { createScriptURL(url: string): string }): { createScriptURL(url: string): unknown };
}

/**
 * The service worker is registered through a Trusted Types policy (the page requires them for scripts): it allows
 * exactly one URL, `/sw.js`, and is the only policy the CSP lets the app make.
 */
function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  const tt = (window as unknown as { trustedTypes?: TrustedTypePolicyFactory }).trustedTypes;
  let url: unknown = '/sw.js';
  try {
    if (tt) {
      const policy = tt.createPolicy('writing-suite-sw', {
        createScriptURL: (u) => {
          if (u !== '/sw.js') throw new TypeError(`not the service worker: ${u}`);
          return u;
        },
      });
      url = policy.createScriptURL('/sw.js');
    }
  } catch {
    // no policy (already made, or not allowed): the plain URL, which the browser may refuse
  }
  navigator.serviceWorker.register(url as string, { scope: '/' }).catch(() => {
    // private windows and some browsers refuse: the app still works, just not offline after a reload
  });
}

async function main() {
  applyAppearance();
  applyContrast();
  applyFace();
  const root = createRoot(document.getElementById('root')!);
  try {
    await A.initializeWasm(__AUTOMERGE_WASM__);
  } catch {
    root.render(
      <main className="calm">
        <h1>Five Rooms couldn’t start</h1>
        <p className="lede">Part of the app didn’t load. If you’re offline, connect and reload once; after that it opens offline too.</p>
      </main>,
    );
    return;
  }
  registerServiceWorker();
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void main();
